/**
 * InviteModal — модалка создания приглашения для нового пользователя.
 *
 * Phase 6 Étap 2: Minimum Viable Onboarding
 */

import { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { useCreateInvite } from '../../api/invites';
import { getErrorMessage } from '../../lib/error-messages';

interface InviteModalProps {
  onClose: () => void;
}

export function InviteModal({ onClose }: InviteModalProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'operator'>('operator');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createInvite = useCreateInvite();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCreate = async () => {
    setError(null);
    try {
      const result = await createInvite.mutateAsync({ email, role });
      const link = `${window.location.origin}/accept-invite?token=${result.inviteToken}`;
      setInviteLink(link);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleCopy = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API недоступен (HTTP, iframe)
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-surface-elevated rounded-panel shadow-xl border border-border-subtle w-full max-w-md mx-4 p-6">
        <h2 className="font-heading text-xl font-semibold text-text-primary mb-1">
          Пригласить в команду
        </h2>
        <p className="text-sm text-text-secondary mb-6">
          Отправьте ссылку-приглашение новому участнику
        </p>

        {!inviteLink ? (
          <>
            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="user@company.com"
              />
            </div>

            {/* Role */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Роль
              </label>
              <div className="flex gap-2">
                <RoleButton
                  label="Оператор"
                  description="Работает с входящими"
                  selected={role === 'operator'}
                  onClick={() => setRole('operator')}
                />
                <RoleButton
                  label="Админ"
                  description="Управляет командой"
                  selected={role === 'admin'}
                  onClick={() => setRole('admin')}
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-accent-danger mb-4">{error}</p>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" size="md" onClick={onClose}>
                Отмена
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleCreate}
                disabled={!email || createInvite.isPending}
              >
                {createInvite.isPending ? 'Создание...' : 'Создать приглашение'}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Success state */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 rounded-full bg-accent-success/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-accent-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-sm font-medium text-text-primary">Приглашение создано</span>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                Отправьте эту ссылку на <strong>{email}</strong>:
              </p>
              <div className="relative">
                <div className="bg-surface-subtle rounded-comp p-3 pr-20 text-xs text-text-secondary font-mono break-all">
                  {inviteLink}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopy}
                  className="absolute top-1.5 right-1.5"
                >
                  {copied ? 'Скопировано!' : 'Копировать'}
                </Button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button variant="primary" size="md" onClick={onClose}>
                Готово
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// RoleButton
// ============================================================================

function RoleButton({
  label,
  description,
  selected,
  onClick,
}: {
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`flex-1 p-3 rounded-comp border text-left transition-all ${
        selected
          ? 'border-accent-brand bg-accent-brand/5'
          : 'border-border-subtle hover:border-border-strong'
      }`}
    >
      <p className={`text-sm font-medium ${selected ? 'text-accent-brand' : 'text-text-primary'}`}>
        {label}
      </p>
      <p className="text-xs text-text-secondary mt-0.5">{description}</p>
    </button>
  );
}
